import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Chip, Grid, Icon, Typography } from "@mui/material";
import cx from 'classnames';
import { Checkbox } from 'Components/uiControls';
import { Button, ContextMenu, Pill, Tooltip } from 'Components';
import { cutNotes } from 'Core/data/Helpers';

import '../styles.scss';

/**
 * Single entity selector component with search and multi-select functionality
 */
const EntityTagPickerSingle = (props) => {
    const { messages } = useIntl();

    const inputRef = useRef();
    const containerRef = useRef();

    const {
        searchText,
        setSearchText,
        data,
        values,
        onChange,
        entity,
        disabled,
        onOpen,
        hiddenCheckbox,
        includeInactiveDrivers
    } = props;

    const [open, setOpen] = useState(false);

    const handleClickOutside = useCallback((e) => {
        if (containerRef?.current?.contains(e.target)) {
            return;
        }
        setOpen(false);
        setSearchText("");
    }, [setSearchText]);

    const openMenu = useCallback((e) => {
        if (e) e.preventDefault();
        setOpen(prev => !prev);
        setTimeout(() => inputRef?.current?.focus(), 0);
    }, []);

    useEffect(() => {
        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [open, handleClickOutside]);

    useEffect(() => {
        if (onOpen) {
            openMenu();
        }
    }, [onOpen, openMenu]);

    const handleItemToggle = useCallback((item) => {
        const itemId = item?.id || item?.driverId;
        let newValues = [...values];

        if (values?.find(v => v?.id === itemId)) {
            newValues = values.filter(v => v?.id !== itemId);
        } else {
            newValues.push({
                id: itemId,
                name: entity?.id,
                label: item?.name || item?.label || item?.userName || "",
                status: item?.status,
                esn: item?.esn,
                deviceModel: item?.deviceModel?.name
            });
        }

        onChange?.(newValues, entity);
    }, [values, entity, onChange]);

    const handleChipDelete = useCallback((value) => {
        const newValues = values.filter(nv => nv.id !== value.id);
        onChange?.(newValues, entity);
    }, [values, entity, onChange]);

    const isItemChecked = useCallback((item) => {
        const itemId = item?.id || item?.driverId;
        return Boolean(values?.find(v => v?.id === itemId));
    }, [values]);

    const getPlaceholder = () => {
        return entity?.id === "Unit"
            ? messages['entityTagPicker_search_unit']
            : messages['entityTagPicker_search_driver'];
    };

    return (
        <div ref={containerRef} open={open} className="gtc-entity-tag-picker-container" style={{
            fontFamily: "Outfit, sans-serif !important"
        }}>
            <div className="gtc-entity-tag-picker-container__input-container">
                {!open && (
                    <>
                        <div
                            className={cx(
                                "gtc-entity-tag-picker-container__value-container",
                                { "gtc-entity-tag-picker-container__value-container--disabled": disabled }
                            )}
                            onClick={!disabled ? openMenu : undefined}
                        >
                            <span className='text-muted'>{getPlaceholder()}</span>
                        </div>

                        {values?.length > 0 && (
                            <div className="gtc-entity-tag-picker-container__chips-container">
                                {values.map((value, index) => (
                                    <Chip
                                        key={`chip-${value.id}-${index}`}
                                        label={cutNotes(value?.label, 15)}
                                        className="gtc-entity-tag-picker-container__chip"
                                        clickable
                                        deleteIcon={<Icon className="gtc-entity-tag-picker-container__chip-icon">close</Icon>}
                                        onDelete={() => handleChipDelete(value)}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {open && (
                    <input
                        ref={inputRef}
                        className="gtc-entity-tag-picker-container__input input-normal form-control"
                        type="text"
                        value={searchText}
                        placeholder={getPlaceholder()}
                        onChange={(e) => setSearchText(e.target.value)}
                        autoFocus
                        disabled={disabled}
                    />
                )}

                <div className="gtc-entity-tag-picker-container__menu-arrow-button" onClick={openMenu}>
                    <Button
                        type="icon"
                        icon={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                        placement="bottom"
                        className="btn-xs"
                        fontSize="inherit"
                        disabled={disabled}
                    />
                </div>
            </div>

            <ContextMenu
                className="gtc-entity-tag-picker-container__context-menu"
                open={open}
            >
                <div className="gtc-entity-tag-picker-container__options-container">
                    {data?.length === 0 && (
                        <div className="text-muted gtc-entity-tag-picker-container__no-items">
                            {messages['entityTagPicker_noItemsFound']}
                        </div>
                    )}

                    {data?.map((item, index) => {
                        const itemId = item?.id || item?.driverId;
                        const checked = isItemChecked(item);
                        const itemLabel = item?.label || item?.name || item?.userName || "";

                        return (
                            <Tooltip title={itemLabel} placement="left">
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    wrap="nowrap"
                                    key={`item-${itemId}-${index}`}
                                    className="gtc-entity-tag-picker-container__item"
                                >
                                    {!hiddenCheckbox && (
                                        <Grid item>
                                            <Checkbox
                                                id={`item-${index}-${itemId}`}
                                                value={checked}
                                                onChange={() => handleItemToggle(item)}
                                            />
                                        </Grid>
                                    )}

                                    <Grid
                                        item
                                        xs
                                        zeroMinWidth
                                        className={cx({ "gtc-entity-tag-picker-container__item-flex": includeInactiveDrivers })}
                                    >

                                        <Typography
                                            noWrap
                                            className={cx("gtc-entity-tag-picker-container__item-label", {
                                                'component-single-inactive': includeInactiveDrivers
                                            })}
                                        >
                                            <span>{itemLabel}</span>
                                        </Typography>


                                        {includeInactiveDrivers && item.status === 0 && (
                                            <div>
                                                <Pill
                                                    textField="label"
                                                    item={{ label: messages['entityTagPicker_inactive'] }}
                                                    clickable={false}
                                                    outlined
                                                    className="gtc-entity-tag-picker-container__inactive-pill"
                                                    colorField="default"
                                                    colorSelection="005bf8"
                                                />
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </Tooltip>
                        );
                    })}
                </div>
            </ContextMenu>
        </div>
    );
};

export default EntityTagPickerSingle;
